import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChefHat, Clock, FileDown, Plus, Search, Wand2 } from "lucide-react";
import { CookbookExport } from "@/components/recipes/CookbookExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { RecipeEditor } from "@/components/recipes/RecipeEditor";
import { RecipeView } from "@/components/recipes/RecipeView";

const Card = ({ r, open, i, showUniverse }) => (
  <button onClick={open} className="group flex flex-col gap-2 rounded-lg border border-[#2D3442] bg-[#0F131A] p-5 text-left transition-all hover:-translate-y-0.5 hover:border-[#D97706]/50" data-testid={`recipe-card-${i}`}>
    <div className="flex items-start gap-2"><ChefHat className="mt-1 h-4 w-4 shrink-0 text-[#F59E0B]" /><span className="font-display text-xl leading-tight text-[#F3EBDD] group-hover:text-[#FBBF24]">{r.title}</span></div>
    {r.description && <p className="line-clamp-2 text-sm text-[#A8A090]">{r.description}</p>}
    <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-xs text-[#7A7468]">
      <span>Serves {r.servings}</span><span>{r.ingredients?.length || 0} ingredients</span>
      {(r.prep_minutes || r.cook_minutes) ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{(r.prep_minutes || 0) + (r.cook_minutes || 0)} min</span> : null}
      {r.ingredients?.some((x) => x.fictional) && <span className="inline-flex items-center gap-0.5 text-[#D8B4FE]"><Wand2 className="h-3 w-3" />made-up ingredients</span>}
      {showUniverse && <span className="text-[#A8A090]">{r.universe_name || "My kitchen"}</span>}
    </div>
  </button>
);

const filterList = (list, q, scope) => {
  const k = q.trim().toLowerCase();
  return list.filter((r) => (scope === "all" || (scope === "mine" ? !r.universe_id : r.universe_id === scope))
    && (!k || [r.title, r.description, ...(r.tags || []), ...(r.ingredients || []).map((i) => i.name)].some((v) => String(v || "").toLowerCase().includes(k))));
};

const Scopes = ({ universes, scope, setScope }) => {
  const chip = (k, l) => <button key={k} onClick={() => setScope(k)} className={`rounded-full px-3 py-1 text-xs transition-colors ${scope === k ? "bg-[#D97706] text-[#0B0D10]" : "border border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`} data-testid={`cookbook-scope-${k}`}>{l}</button>;
  return <div className="flex flex-wrap gap-1.5">{chip("all", "All recipes")}{chip("mine", "My kitchen")}{universes.map((u) => chip(u.id, u.name))}</div>;
};

const NewPick = ({ universes, onPick }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#D97706]/40 bg-[#D97706]/5 p-3 text-sm animate-in fade-in duration-200" data-testid="cookbook-new-pick">
    <span className="text-[#F3EBDD]">Where does this recipe belong?</span>
    <Button size="sm" variant="outline" onClick={() => onPick(null)} data-testid="cookbook-new-mine">My kitchen</Button>
    {universes.filter((u) => u.can_edit).map((u) => <Button key={u.id} size="sm" variant="outline" onClick={() => onPick(u.id)} data-testid={`cookbook-new-${u.id}`}>{u.name}</Button>)}
  </div>
);

export const RecipeBook = ({ basePath, universeId, canEdit = true, title, eyebrow }) => {
  const { rid } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const cookbook = !universeId;
  const key = cookbook ? ["cookbook"] : ["recipes", universeId];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api.get(cookbook ? "/recipes" : `/universes/${universeId}/recipes`).then((r) => r.data) });
  const list = useMemo(() => (cookbook ? data?.recipes : data) || [], [data, cookbook]);
  const universes = cookbook ? data?.universes || [] : [];
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["cookbook"] }); qc.invalidateQueries({ queryKey: ["recipes"] }); };
  const go = (id) => { setEditing(false); nav(id ? `${basePath}/${id}` : basePath); };

  if (rid === "new" && !canEdit) return <p className="text-sm text-[#A8A090]" data-testid="recipe-no-access">You can read this universe's recipes, but only owners and editors can add new ones.</p>;
  if (rid === "new") return <RecipeEditor universeId={universeId || sp.get("u") || null} entityId={sp.get("food")} onSaved={(r) => { refresh(); go(r.id); }} onCancel={() => go(null)} />;
  if (rid) {
    const r = list.find((x) => x.id === rid);
    if (!r) return <p className="text-sm text-[#7A7468]" data-testid="recipe-missing">{isLoading ? "Loading…" : "This recipe isn't here any more."}</p>;
    if (editing) return <RecipeEditor recipe={r} onSaved={() => { refresh(); setEditing(false); }} onCancel={() => setEditing(false)} />;
    return <RecipeView key={r.updated_at} recipe={r} canEdit={canEdit && r.can_edit !== false} back={() => go(null)} backLabel={cookbook ? "Cookbook" : "All recipes"} onEdit={() => setEditing(true)} onDeleted={() => { refresh(); go(null); }} />;
  }
  const shown = filterList(list, q, scope);
  const startNew = () => (cookbook ? setPicking(!picking) : nav(`${basePath}/new`));
  return (
    <div className="space-y-6" data-testid="recipe-book">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><div className="lf-label mb-2">{eyebrow}</div><h1 className="font-display text-4xl tracking-tight text-[#F3EBDD] sm:text-5xl">{title}</h1></div>
        <div className="flex gap-2">
          {cookbook && list.length > 0 && <Button variant="outline" onClick={() => setExporting(true)} data-testid="cookbook-export-button"><FileDown className="mr-1.5 h-4 w-4" />Print cookbook</Button>}
          {canEdit && <Button onClick={startNew} data-testid="recipe-new-button"><Plus className="mr-1.5 h-4 w-4" />New recipe</Button>}
        </div>
      </div>
      {cookbook && <CookbookExport open={exporting} onOpenChange={setExporting} recipes={list} universes={universes} />}
      {picking && <NewPick universes={universes} onPick={(u) => nav(`${basePath}/new${u ? `?u=${u}` : ""}`)} />}
      <div className="flex flex-col gap-3">
        <div className="relative sm:max-w-sm"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A8A090]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipes, ingredients, tags" className="pl-9" data-testid="recipe-search" /></div>
        {cookbook && <Scopes universes={universes} scope={scope} setScope={setScope} />}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{shown.map((r, i) => <Card key={r.id} r={r} i={i} open={() => go(r.id)} showUniverse={cookbook} />)}</div>
      {!isLoading && !shown.length && (
        <div className="rounded-lg border border-dashed border-[#2D3442] p-10 text-center" data-testid="recipe-empty">
          <ChefHat className="mx-auto h-8 w-8 text-[#F59E0B]" />
          <p className="mt-3 text-sm text-[#A8A090]">{list.length ? "No recipes match." : "No recipes yet. Write down a dish from your world, or one from your own kitchen."}</p>
          <p className="mt-1 text-xs text-[#7A7468]">Scale it to feed a crowd and get health stats. The AI never writes recipes for you.</p>
        </div>
      )}
    </div>
  );
};
